import React, {useState} from "react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {Loader} from "lucide-react";
import {ListLoader, Panel, SummaryStrip, ToolbarDate} from "../../stock/StockUI";
import {fetchReturnsReport, formatMoney} from "./returnsApi";
import {defaultPeriod} from "./ReturnsListTab";

// Ranked single-series breakdown: one hue, value labelled next to each bar
// (no legend — the section title names the series).
const BarList = ({title, rows, valueKey = "amount", format = formatMoney, detail, empty = "Aucune donnée"}) => {
    const max = Math.max(0, ...rows.map((r) => Number(r[valueKey]) || 0));
    return (
        <Panel title={title}>
            {rows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">{empty}</p>
            ) : (
                <ul className="space-y-2.5 px-4 sm:px-6 py-4">
                    {rows.map((row, i) => {
                        const value = Number(row[valueKey]) || 0;
                        const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
                        return (
                            <li key={`${row.label || row.name}-${i}`}
                                title={`${row.label || row.name} : ${format(value)}${detail ? ` · ${detail(row)}` : ""}`}>
                                <div className="flex items-baseline justify-between gap-3 text-sm">
                                    <span className="text-gray-700 dark:text-gray-300 truncate">{row.label || row.name}</span>
                                    <span className="text-gray-900 dark:text-gray-100 font-medium tabular-nums whitespace-nowrap">
                                        {format(value)}
                                        {detail && <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{detail(row)}</span>}
                                    </span>
                                </div>
                                <div className="mt-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700">
                                    <div className="h-2 rounded-full bg-primary" style={{width: `${pct}%`}}/>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Panel>
    );
};

const qty = (row) => `${Number(row.quantity).toLocaleString("fr-FR")} art.`;

const ReturnsReportTab = () => {
    const [period, setPeriod] = useState(defaultPeriod);

    const {data, isLoading, isError, isFetching} = useQuery({
        queryKey: ["returns-report", period],
        queryFn: () => fetchReturnsReport(period),
        placeholderData: keepPreviousData,
    });

    const fr = (n) => Number(n || 0).toLocaleString("fr-FR");

    return (
        <div className="max-w-full">
            <div className="flex items-stretch">
                <ToolbarDate label="Du" value={period.from} max={period.to} className="flex-1 sm:flex-none sm:w-44"
                             onChange={(v) => setPeriod((p) => ({...p, from: v}))}/>
                <ToolbarDate label="Au" value={period.to} min={period.from} className="flex-1 sm:flex-none sm:w-44"
                             onChange={(v) => setPeriod((p) => ({...p, to: v}))}/>
                <div className="hidden sm:flex flex-1 items-center px-4 border-y border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">
                    {isFetching && !isLoading
                        ? <span className="flex items-center gap-2"><Loader className="w-4 h-4 animate-spin text-primary"/> Mise à jour…</span>
                        : "Période du rapport"}
                </div>
            </div>

            {isLoading ? (
                <ListLoader label="Chargement du rapport..."/>
            ) : isError ? (
                <p className="text-sm text-red-500 p-6 text-center">Impossible de charger le rapport.</p>
            ) : (
                <>
                    <SummaryStrip items={[
                        {key: "amount", label: "Montant retourné", value: formatMoney(data.returned_amount),
                            color: "text-rose-600 dark:text-rose-400",
                            hint: `${fr(data.returned_quantity)} article(s), dont ${fr(data.restocked_quantity)} remis en stock`},
                        {key: "rate", label: "Taux de retour",
                            value: `${data.return_rate.toLocaleString("fr-FR", {maximumFractionDigits: 1})} %`,
                            hint: `des ventes de la période (${formatMoney(data.gross_sales)})`},
                        {key: "returns", label: "Retours", value: fr(data.returns_count)},
                        {key: "exchanges", label: "Échanges", value: fr(data.exchanges_count)},
                    ]}/>
                    <SummaryStrip items={[
                        {key: "issued", label: "Avoirs émis", value: formatMoney(data.credit_notes.issued_amount),
                            hint: `${data.credit_notes.issued_count} avoir(s)`},
                        {key: "used", label: "Avoirs utilisés", value: formatMoney(data.credit_notes.used_amount),
                            hint: `${data.credit_notes.used_count} avoir(s)`},
                        {key: "outstanding", label: "Avoirs en circulation", value: formatMoney(data.credit_notes.outstanding_amount),
                            color: "text-primary", hint: "encore utilisables aujourd'hui"},
                    ]}/>

                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        <BarList title="Remboursements par mode" rows={data.refunds_by_mode}/>
                        <BarList title="Par motif" rows={data.by_reason} detail={qty}/>
                        <BarList title="Produits les plus retournés" rows={data.top_products} detail={qty}/>
                        <BarList title="Par employé" rows={data.by_employee} detail={(row) => `${row.count} opération(s)`}/>
                    </div>
                </>
            )}
        </div>
    );
};

export default ReturnsReportTab;
